import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useStores} from "../../../../stores/useStores";
import {observer} from "mobx-react-lite";
import {useAsync} from "react-async";
import CreateEditEventTemplate from "../../../../components/templates/events/createEditEventTemplate";
import {useTranslation} from "react-i18next";
import {ToastMessageStatus} from "../../../../components/atoms/toastMessage/toastMessage";
import {useHistory, useParams} from "react-router";
import {RoutePaths} from "../../../../shared/routePaths";
import Skeleton from "react-loading-skeleton";
import "./editDeleteEvent.sass";
import {runInAction} from "mobx";
import {QueryParams} from "../../../../shared/enums/queryParams";
import {Navigation} from "../../../../utils/navigation";
import {EventStatus} from "../../../../shared/enums/eventStatus";
import {UpdateRecurringEventOption} from "../../../../shared/enums/updateRecurringEventOption";
import {format} from "date-fns";
import EventPublishModal from "../../../../components/organisms/eventPublishModal/eventPublishModal";
import CancelPostponeModal from "../../../../components/organisms/cancelPostponeModal/cancelPostponeModal";
import PreReleaseConfirmationModal from "../../../../components/organisms/preReleaseConfirmationModal/preReleaseConfirmationModal";
import {utcToZonedTime} from "date-fns-tz";
import DeleteSeriesModal from "../../../../components/organisms/deleteSeriesModal/deleteSeriesModal";
import {getEventsUrl} from "../../../../utils/urlUtils";

// testing this component is too much like integration as opposed to unit
/* istanbul ignore file */

const EditDeleteEvent: React.FC = () => {
  const history = useHistory();
  const {t} = useTranslation(["translation", "event"]);
  const location = history.location;
  const {id} = useParams<{id: string}>();
  const [isEventPublishModalOpen, setIsEventPublishModalOpen] = useState(false);
  const [
    isPreReleaseConfirmationModalOpen,
    setIsPreReleaseConfirmationModalOpen
  ] = useState(false);
  const [isDeleteSeriesModalOpen, setIsDeleteSeriesModalOpen] = useState(false);

  const {
    uiStore: {modalStore, toastMessageStore, eventsUiStore},
    domainStore: {eventsDomainStore}
  } = useStores();

  const {
    isInitial,
    isLoading,
    isPending,
    run: reloadEventAsync
  } = useAsync({
    deferFn: (args) => {
      const queryParams = new URLSearchParams(location.search);
      const villageId = queryParams.get(QueryParams.VillageId);
      return eventsUiStore.reloadEventDetailsAndBookings(
        args[0] ?? id,
        villageId ?? undefined
      );
    },
    onReject: () => {
      toastMessageStore.displayToastMessage(
        t("event:messages.invalidEditableEvent"),
        ToastMessageStatus.Danger,
        8000
      );
      eventsUiStore.resetIsCreatingOrEditingFlag();
      navigateBackToCalendar();
    }
  });

  const navigateBackToCalendar = () => {
    Navigation.go(history, RoutePaths.Events);
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const occurrenceDate = queryParams.get(QueryParams.OccurrenceDate);
    const isEditingEventSeries = queryParams.get(QueryParams.EditSeries);

    if (occurrenceDate || isEditingEventSeries) {
      const updateOption = occurrenceDate
        ? UpdateRecurringEventOption.OccurrenceDate
        : UpdateRecurringEventOption.Series;
      eventsDomainStore.setEventUpdateOption(updateOption);
    } else {
      // clear previously selected value
      eventsDomainStore.setEventUpdateOption(undefined);
    }

    eventsDomainStore.setOccurrenceDate(occurrenceDate);
    eventsUiStore.setEditingEventSeries(!!isEditingEventSeries);
  }, []);

  useEffect(() => {
    eventsDomainStore.getVillages(eventsUiStore.selectedCalendarName);
  }, [
    eventsDomainStore,
    eventsDomainStore.editableEvent,
    eventsUiStore.selectedCalendarName
  ]);

  const saveEvent = useAsync({
    deferFn: eventsDomainStore.editEvent,
    onReject: (e) => {
      if (e?.cause === "Event end time elapsed") {
        toastMessageStore.displayToastMessage(
          t("event:messages.eventNotSaved"),
          ToastMessageStatus.Danger
        );

        navigateBackToCalendar();
        return;
      }
      toastMessageStore.displayToastMessage(
        t("event:messages.updateEventFailure", {
          eventTitle: eventsDomainStore.editableEvent.title.text
        }),
        ToastMessageStatus.Danger
      );
    },
    onResolve: (eventTitle) => {
      toastMessageStore.displayToastMessage(
        t("event:messages.updatedEvent", {eventTitle: eventTitle}),
        ToastMessageStatus.Success
      );
      navigateBackToCalendar();
    }
  });

  const deleteEvent = useAsync({
    deferFn: eventsDomainStore.deleteEvent,
    onReject: () =>
      toastMessageStore.displayToastMessage(
        t("event:messages.deletedEventFailure", {
          eventTitle: eventsDomainStore.editableEvent.title.text
        }),
        ToastMessageStatus.Danger
      ),
    onResolve: (eventTitle) => {
      eventsDomainStore.resetEditableEvent();
      toastMessageStore.displayToastMessage(
        t("event:messages.deletedEvent", {eventTitle: eventTitle}),
        ToastMessageStatus.Success
      );
      navigateBackToCalendar();
    }
  });

  const deleteEventSeries = useAsync({
    deferFn: eventsDomainStore.deleteEventSeries,
    onReject: () =>
      toastMessageStore.displayToastMessage(
        t("event:messages.deletedEventFailure", {
          eventTitle: eventsDomainStore.editableEvent.title.text
        }),
        ToastMessageStatus.Danger
      ),
    onResolve: (response) => {
      let toastFromDate;
      if (response.occurrenceDate == undefined) {
        toastFromDate = eventsDomainStore.editableEvent.originalStartDate!;
      } else {
        toastFromDate = new Date(response.occurrenceDate);
      }
      eventsDomainStore.resetEditableEvent();
      toastMessageStore.displayToastMessage(
        t("event:messages.deletedEventSeries", {
          eventTitle: response.eventTitle,
          occurrenceDate: format(
            utcToZonedTime(toastFromDate, eventsDomainStore.getTimeZone()),
            "dd MMMM yyyy"
          )
        }),
        ToastMessageStatus.Success
      );
      navigateBackToCalendar();
    }
  });

  const cancelPostponeEvent = useAsync({
    deferFn: (args) => eventsDomainStore.cancelPostponeEvent(args[0]),
    onReject: () =>
      toastMessageStore.displayToastMessage(
        t("event:messages.cancelledPostponedEventFailure", {
          eventTitle: eventsDomainStore.editableEvent.title.text
        }),
        ToastMessageStatus.Danger
      ),
    onResolve: (event) => {
      // When canceling or postponing a specific instance of a recurring event, a distinct, detached occurrence
      // is generated. In this case, it is necessary to update the occurrence date to ensure that the detached instance
      // is properly reloaded.
      if (eventsDomainStore.occurrenceDate) {
        eventsDomainStore.setOccurrenceDate(undefined);
        eventsDomainStore.setEventUpdateOption(undefined);

        const villageIdQueryParam = `${QueryParams.VillageId}=${eventsUiStore.selectableVillages?.selectedItem?.value}`;
        let defaultUrl = `${RoutePaths.Events}/events/${event.id.id}?${villageIdQueryParam}`;
        Navigation.go(history, defaultUrl);
      }

      const [title, status] = [
        eventsDomainStore.editableEvent.title.text,
        eventsDomainStore.editableEvent.eventStatus
      ];

      toastMessageStore.displayToastMessage(
        t("event:messages.cancelledPostponedEvent", {
          eventTitle: title,
          eventStatus: status
        }),
        ToastMessageStatus.Success
      );

      // redirect to manage booking page when has paid bookings, or back to calendar view
      if (eventsDomainStore.editableEvent.hasPaidAttendees) {
        reloadEventAsync(event.id.id); // sync local data with server
        const searchParams = Navigation.getCurrentSearchParams(history);
        searchParams.set("tab", t("event:titles.manageBookings"));
        history.push({
          pathname: history.location.pathname,
          search: searchParams.toString()
        });
      } else {
        navigateBackToCalendar();
      }
    }
  });
  const villageIdQueryParam = `${QueryParams.VillageId}=${eventsUiStore.selectableVillages?.selectedItem?.value}`;
  const eventSeriesId = eventsDomainStore.editableEvent.getEventSeriesId();

  const showLoadBookingsError = useCallback(() => {
    toastMessageStore.displayToastMessage(
      t("event:messages.loadBookingsFailure", {
        eventTitle: eventsDomainStore.editableEvent.title.text
      }),
      ToastMessageStatus.Danger
    );
  }, []);

  const loadEventBookingDetailsCallback = useCallback(
    async (eventId: string) => {
      try {
        await eventsUiStore.loadEventBookings(eventId);
      } catch (error) {
        showLoadBookingsError();
      }
    },
    [showLoadBookingsError, eventsUiStore]
  );

  useEffect(() => {
    runInAction(() => {
      eventsUiStore.isCreatingOrEditing = true;
    });
  }, [eventsUiStore]);

  const selectedCalendarName = Navigation.getQueryParamByKey(
    history,
    QueryParams.calendar
  );

  useEffect(() => {
    runInAction(() => {
      eventsUiStore.selectedCalendarName = selectedCalendarName;
      eventsUiStore.selectedCalendarType =
        eventsUiStore.calendarTypeBySelectedCalendarName;
    });
    if (id && !isLoading && !isPending && isInitial) {
      reloadEventAsync();
    }
  }, [
    id,
    isLoading,
    isPending,
    isInitial,
    reloadEventAsync,
    eventsUiStore,
    selectedCalendarName
  ]);

  const params = useMemo(
    () => new URLSearchParams(history.location.search),
    [history.location.search]
  );

  /***
   * Listen to reason's changes and disable primary button when invalid.
   */
  const [cancelPostponeModal, setCancelPostponeModal] = useState({
    isOpen: false,
    eventStatus: EventStatus.Published
  });

  useEffect(() => {
    if (cancelPostponeModal.isOpen) {
      runInAction(() => {
        modalStore.isPrimaryButtonDisabled = !(
          eventsDomainStore.editableEvent.reason.isValid &&
          eventsDomainStore.editableEvent.reason.validate?.(
            eventsDomainStore.editableEvent.reason.text
          )
        );
      });
    } else {
      // clean up on closing
      setCancelPostponeModal({
        isOpen: false,
        eventStatus: cancelPostponeModal.eventStatus
      });
      runInAction(() => {
        modalStore.isPrimaryButtonDisabled = false;
      });
    }
  }, [cancelPostponeModal.isOpen]);

  /**
   * This method creates a new start and end date for an event, using the effective date chosen
   * during the cancellation or modification of a released event.
   * @param effectiveDate
   */
  const handleEffectiveSelection = (effectiveDate?: Date | null) => {
    if (
      !effectiveDate ||
      !eventsUiStore.isEditingEventSeries ||
      eventsDomainStore.eventUpdateOption === UpdateRecurringEventOption.Series
    )
      return;

    eventsDomainStore.occurrenceDate = effectiveDate.toISOString();
    const eventStartDate =
      eventsDomainStore.editableEvent.eventDateDetails.startDate.date!;
    const eventEndDate =
      eventsDomainStore.editableEvent.eventDateDetails.endDate.date!;

    const duration = eventEndDate.getTime() - eventStartDate.getTime();
    let endDate = new Date(effectiveDate.valueOf() + duration);

    let newEventStartDate = new Date(
      effectiveDate.getFullYear(),
      effectiveDate.getMonth(),
      effectiveDate.getDate(),
      eventStartDate.getHours(),
      eventStartDate.getMinutes(),
      eventStartDate.getSeconds()
    );
    let newEventEndDate = new Date(
      endDate.getFullYear(),
      endDate.getMonth(),
      endDate.getDate(),
      eventEndDate.getHours(),
      eventEndDate.getMinutes(),
      eventEndDate.getSeconds()
    );

    eventsDomainStore.editableEvent.eventDateDetails.startDate.date =
      newEventStartDate;
    eventsDomainStore.editableEvent.eventDateDetails.endDate.date =
      newEventEndDate;
  };
  const currentTime = utcToZonedTime(
    new Date(),
    eventsDomainStore.editableEvent.timeZone
  ).getTime();

  const runPublishFlow = () => {
    if (
      eventsDomainStore.editableEvent.releaseDate?.date !== null &&
      eventsDomainStore.editableEvent.releaseDate.date.getTime() > currentTime
    ) {
      setIsPreReleaseConfirmationModalOpen(true);
    } else {
      if (
        eventsUiStore.isEditingEventSeries &&
        eventsDomainStore.editableEvent.hasChangesIgnoringRecurrenceEndDate
      ) {
        setIsEventPublishModalOpen(true);
      } else {
        const eventDateChanged =
          eventsDomainStore.editableEvent.eventDateDetails.hasDateChange();
        eventsDomainStore.editableEvent.isDeadLinePassed &&
        eventsDomainStore.editableEvent.isBookingReopen &&
        (eventsDomainStore.editableEvent.eventDateDetails.hasDateChange() ||
          eventsDomainStore.editableEvent.bookingDetails.hasBookingDeadlineChange())
          ? modalStore.openModalWithContent({
              content: eventDateChanged
                ? t("event:messages.changeDate")
                : t("event:messages.changeDeadline"),
              title: eventDateChanged
                ? t("event:titles.changeDate")
                : t("event:titles.changeDeadline"),
              primaryButtonText: eventDateChanged
                ? t("translation:confirmations.affirmativeChangeDate")
                : t("translation:confirmations.affirmativeDeadline"),
              secondaryButtonText: t("translation:confirmations.negative"),
              onPrimaryClick: () => {
                saveEvent.run();
                modalStore.toggle();
              },
              onSecondaryClick: modalStore.toggle
            })
          : saveEvent.run();
      }
    }
  };

  const publishEvent = () => {
    void (async () => {
      if (!eventsUiStore.calendarSchedule?.startDate) {
        await eventsUiStore.loadEventReleaseDate();
      }

      runPublishFlow();
    })();
  };

  return (
    <>
      {eventsDomainStore.editableEvent.releaseDate.date && (
        <PreReleaseConfirmationModal
          toggle={() => setIsPreReleaseConfirmationModalOpen(false)}
          isOpen={isPreReleaseConfirmationModalOpen}
          onPublishBtnClicked={() => {
            saveEvent.run();
            setIsPreReleaseConfirmationModalOpen(false);
          }}
          isSaving={saveEvent.isPending}
          preReleaseDate={format(
            utcToZonedTime(
              eventsDomainStore.editableEvent.releaseDate.date!,
              eventsDomainStore.editableEvent.timeZone
            ),
            "dd/MM/yyyy"
          )}
        />
      )}
      <CancelPostponeModal
        toggle={() =>
          setCancelPostponeModal({
            isOpen: !cancelPostponeModal.isOpen,
            eventStatus: cancelPostponeModal.eventStatus
          })
        }
        isOpen={cancelPostponeModal.isOpen}
        eventStatus={cancelPostponeModal.eventStatus}
        onConfirmBtnClicked={(effectiveFromDate: Date) => {
          if (eventsUiStore.isEditingEventSeries) {
            const eventDetail = eventsDomainStore.editableEvent;
            const updatingEventFromFirstOccurrence =
              eventDetail.originalStartDate?.getTime() ===
              effectiveFromDate.getTime();

            eventsDomainStore.eventUpdateOption =
              updatingEventFromFirstOccurrence
                ? UpdateRecurringEventOption.Series
                : UpdateRecurringEventOption.FromOccurrenceDate;

            if (
              eventsDomainStore.eventUpdateOption ===
              UpdateRecurringEventOption.FromOccurrenceDate
            ) {
              eventsDomainStore.occurrenceDate =
                effectiveFromDate.toISOString();
            }
          }

          if (eventsDomainStore.editableEvent.reason.isValid) {
            cancelPostponeEvent.run(cancelPostponeModal.eventStatus);
            setCancelPostponeModal({
              isOpen: !cancelPostponeModal.isOpen,
              eventStatus: cancelPostponeModal.eventStatus
            });
          }
        }}
      />
      <DeleteSeriesModal
        toggle={() => setIsDeleteSeriesModalOpen(!isDeleteSeriesModalOpen)}
        isOpen={isDeleteSeriesModalOpen}
        onConfirmBtnClicked={(effectiveFromDate: Date) => {
          const eventDetail = eventsDomainStore.editableEvent;
          const deletingSeriesFromFirstOccurrence =
            eventDetail.originalStartDate?.getTime() ===
            effectiveFromDate.getTime();

          // Set occurrence date to delete from if not deleting entire series
          if (!deletingSeriesFromFirstOccurrence) {
            eventsDomainStore.occurrenceDate = effectiveFromDate.toISOString();
          }
          deleteEventSeries.run();
          setIsDeleteSeriesModalOpen(false);
        }}
      />
      <EventPublishModal
        toggle={() => setIsEventPublishModalOpen(!isEventPublishModalOpen)}
        isOpen={isEventPublishModalOpen}
        onConfirmBtnClicked={(effectiveFromDate: Date) => {
          const eventDetail = eventsDomainStore.editableEvent;
          const updatingEventFromFirstOccurrence =
            eventDetail.originalStartDate?.getTime() ===
            effectiveFromDate.getTime();

          const updateEventSeriesWithoutSplit =
            updatingEventFromFirstOccurrence &&
            (!eventDetail.isReleased || !eventDetail.hasNonCosmeticChanges);

          runInAction(() => {
            eventsDomainStore.setEventUpdateOption(
              updateEventSeriesWithoutSplit
                ? UpdateRecurringEventOption.Series
                : UpdateRecurringEventOption.FromOccurrenceDate
            );

            handleEffectiveSelection(effectiveFromDate);
            saveEvent.run();
          });

          setIsEventPublishModalOpen(!isEventPublishModalOpen);
        }}
      />
      {isLoading || isPending ? (
        <Skeleton className="skeleton-view" />
      ) : (
        <CreateEditEventTemplate
          isSaving={saveEvent.isPending}
          isEditing={true}
          isDeleting={deleteEvent.isPending}
          isCancellingPostponing={cancelPostponeEvent.isPending || isLoading}
          publishAction={publishEvent}
          deleteAction={() => {
            // check both stores as they are updated at different times
            // eventsDomainStore is set on load
            // eventsUiStore is set on booking change
            if (
              eventsDomainStore.editableEvent.bookingSpacesDetails
                ?.totalBookings > 0 ||
              eventsDomainStore.editableEvent.bookingSpacesDetails
                ?.highestBookingCountForSeries > 0 ||
              eventsUiStore.confirmedEventBookings?.length > 0 ||
              eventsUiStore.waitlistEventBookings?.length > 0
            ) {
              modalStore.openModalWithContent({
                content: t("event:messages.confirmDeleteEventWhenBookings"),
                title: t("event:titles.deleteEventBlocked"),
                primaryButtonText: t(
                  eventsDomainStore.editableEvent.hasChanges
                    ? "event:actions.confirmDeleteWhenChanges"
                    : "event:actions.confirmDeleteWhenChanges"
                ),
                centered: true,
                dangerButton: true,
                onPrimaryClick: () => {
                  params.delete(QueryParams.Tab);
                  params.append(
                    QueryParams.Tab,
                    encodeURI(t("event:titles.manageBookings"))
                  );
                  history.push({
                    ...history.location,
                    search: params.toString()
                  });
                  modalStore.toggle();
                }
              });
            } else {
              const modalContent = eventsUiStore.isEditingEventSeries
                ? t("event:messages.confirmDeleteEventSeries")
                : t("event:messages.confirmDeleteEvent");
              const modalTitle = eventsUiStore.isEditingEventSeries
                ? t("event:titles.deleteSeries")
                : t("event:titles.delete");

              modalStore.openModalWithContent({
                content: modalContent,
                title: modalTitle,
                primaryButtonText: t("event:actions.confirmDelete"),
                secondaryButtonText: t("translation:confirmations.negative"),
                centered: true,
                dangerButton: true,
                onPrimaryClick: () => {
                  deleteEvent.run();
                  modalStore.toggle();
                },
                onSecondaryClick: modalStore.toggle
              });
            }
          }}
          deleteSeriesAction={() => {
            setIsDeleteSeriesModalOpen(true);
          }}
          editAction={() => {
            history.push(
              getEventsUrl(
                eventSeriesId!,
                villageIdQueryParam,
                eventsUiStore.selectedCalendarName,
                t("event:titles.editEventSeries"),
                null,
                true
              )
            );
          }}
          viewAction={() => {
            history.push(
              getEventsUrl(
                eventSeriesId!,
                villageIdQueryParam,
                eventsUiStore.selectedCalendarName,
                t("event:titles.viewEventSeries"),
                null,
                false
              )
            );
          }}
          saveCancelledPostponedEventAction={() => {
            const status = eventsDomainStore.editableEvent.eventStatus;
            return cancelPostponeEvent.run(status);
          }}
          openCancelPostponeModalAction={(eventStatus: EventStatus) => {
            if (
              eventStatus !== EventStatus.Cancelled &&
              eventStatus !== EventStatus.Postponed
            )
              return;
            setCancelPostponeModal({isOpen: true, eventStatus: eventStatus});
          }}
          onChangeTemplate={() => {}}
          loadEventBookingDetails={loadEventBookingDetailsCallback}
        />
      )}
    </>
  );
};

export default observer(EditDeleteEvent);
